import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { detectDefaultLocale } from "@/lib/signupI18n";

/*
 * Google OAuth landing page.
 *
 * The backend (/api/auth/google/callback) has already validated the OAuth
 * response, upserted the user and set the HttpOnly session cookie before
 * redirecting the browser here. All we do is load the session and hand
 * over to ProtectedRoute (/consent → /setup → /waiting-beta → app).
 * Failures never land here — the backend sends them to /login?google_error=.
 */

const STRINGS = {
  "pt-BR": {
    finalizing: "Finalizando o login com Google…",
    failedTitle: "Falha no login com Google",
    back: "Voltar para o login",
  },
  en: {
    finalizing: "Finishing your Google sign-in…",
    failedTitle: "Google sign-in failed",
    back: "Back to sign in",
  },
  "es-419": {
    finalizing: "Finalizando el inicio de sesión con Google…",
    failedTitle: "No se pudo iniciar sesión con Google",
    back: "Volver al inicio de sesión",
  },
  "fr-CA": {
    finalizing: "Finalisation de la connexion Google…",
    failedTitle: "Échec de la connexion Google",
    back: "Retour à la connexion",
  },
};

function pickStrings() {
  return STRINGS[detectDefaultLocale()] || STRINGS.en;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [err, setErr] = useState("");
  const t = pickStrings();

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    (async () => {
      try {
        const u = await refresh();
        if (!u) throw new Error("session_not_established");
        // Admins land on the dashboard. Other users land on the app and let
        // ProtectedRoute handle consent/setup/waiting-beta requirements.
        navigate(u?.is_admin ? "/admin" : "/", { replace: true });
      } catch {
        setErr(t.failedTitle);
      }
    })();
  }, [navigate, refresh, t.failedTitle]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 text-sm text-[color:var(--text-secondary)]"
      data-testid="auth-callback"
    >
      {err ? (
        <div className="max-w-md rounded-2xl border border-[color:var(--score-bad)]/30 bg-[color:var(--score-bad)]/10 p-4 text-[color:var(--score-bad)]">
          <div className="font-medium mb-2">{t.failedTitle}</div>
          <div>{err}</div>
          <button
            type="button"
            className="mt-3 rounded-full border border-[color:var(--score-bad)]/40 px-4 py-1.5 text-xs"
            onClick={() => navigate("/login", { replace: true })}
          >
            {t.back}
          </button>
        </div>
      ) : (
        <div>{t.finalizing}</div>
      )}
    </div>
  );
}
